// S1 REQ-01: eraser swipe session hook (owned by useWidgetForkInteraction).
import { useCallback, useRef } from "react";
import { createEraseSession } from "../utils/eraseSession";
export function useForkEraseSession(deps) {
    const { engineInstanceRef } = deps;
    const sessionRef = useRef(null);
    const hitIdAt = useCallback((localX, localY) => {
        const hit = engineInstanceRef.current?.hitTestDrawingPrimitive(localX, localY) ?? null;
        const drawingId = hit?.drawingId;
        if (typeof drawingId !== "string") {
            return null;
        }
        return deps.resolvedDrawings.some((drawing) => drawing.id === drawingId) ? drawingId : null;
    }, [engineInstanceRef, deps.resolvedDrawings]);
    const begin = useCallback((pointerContext) => {
        const session = createEraseSession();
        sessionRef.current = session;
        const hitId = hitIdAt(pointerContext.localX, pointerContext.localY);
        if (hitId) {
            session.add(hitId);
        }
        engineInstanceRef.current?.setOverlayInputs({ interactionPanLocked: true });
    }, [engineInstanceRef, hitIdAt]);
    const move = useCallback((pointerContext) => {
        const session = sessionRef.current;
        if (!session) {
            return;
        }
        const hitId = hitIdAt(pointerContext.localX, pointerContext.localY);
        if (hitId) {
            session.add(hitId);
        }
    }, [hitIdAt]);
    const end = useCallback(() => {
        const session = sessionRef.current;
        sessionRef.current = null;
        engineInstanceRef.current?.setOverlayInputs({ interactionPanLocked: false });
        if (!session || session.size === 0) {
            return;
        }
        const { remaining, deleted } = session.finish(deps.resolvedDrawings);
        if (deleted.length === 0) {
            return;
        }
        deps.drawingWorkspace.interaction.replaceDrawings(remaining);
        const selectedIds = deps.drawingWorkspace.state.selectedIds;
        if (selectedIds.some((id) => session.has(id))) {
            deps.drawingWorkspace.selectDrawing(null);
        }
    }, [deps.drawingWorkspace, deps.resolvedDrawings, engineInstanceRef]);
    const cancel = useCallback(() => {
        sessionRef.current = null;
        engineInstanceRef.current?.setOverlayInputs({ interactionPanLocked: false });
    }, [engineInstanceRef]);
    const isActive = useCallback(() => sessionRef.current !== null, []);
    return { begin, move, end, cancel, isActive };
}
